import React, { useEffect } from 'react';
/**
 * `PaymentAssistSuccess` handles the return/redirect from Payment Assist when a
 * payment has completed.
 *
 * Flow:
 * - Reads the Payment Assist token/id from query string.
 * - Fetches the serialized journey state via `PaymentApi.getPaymentAssistState`
 *   and restores it into the journey via `onSetJourney`.
 * - Triggers policy registration via `onRegister` once journey state is
 *   available.
 * - Navigates to confirmation or failure routes based on `registrationState`.
 */
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../app-state/Hooks';
import { setLoggedIn } from '../../app-state/slices/UserSlice';
import { PaymentApi } from '../../api/PaymentApi';
import { PageRelativePath, RegistrationState } from '../../consts/Common';
import { JourneyState, PaymentAssistResponse } from '../common/CommonTypes';
import LoadingContainer from '../common/LoadingContainer/LoadingContainer';
import PageHeader from '../common/PageHeader';
import { useJourney } from './Layout';

const PaymentAssistSuccess = (): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('token') ?? searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { journey, onRegister, onSetJourney } = useJourney();
  const { personalDetail, registrationState } = journey;

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchJourneyState(signal: AbortSignal) {
      const convertJsonToJourneyState = (
        json: string
      ): JourneyState | undefined => {
        try {
          const journeyState: JourneyState = JSON.parse(json);

          return journeyState;
        } catch (e) {
          console.log('Handle error', e);

          return undefined;
        }
      };

      const fetchJourneyStateSuccess = (response: PaymentAssistResponse) => {
        if (response.success) {
          const journeyState = convertJsonToJourneyState(response.journeyState);

          if (!journeyState) {
            fetchJourneyStateFailure();
          } else {
            onSetJourney(journeyState);
            dispatch(setLoggedIn(true));
          }
        } else {
          fetchJourneyStateFailure();
        }
      };

      const fetchJourneyStateFailure = () => {
        navigate(PageRelativePath.PaymentAssistFailure);
      };

      await new PaymentApi().getPaymentAssistState(
        id,
        fetchJourneyStateSuccess,
        fetchJourneyStateFailure,
        signal
      );
    }

    fetchJourneyState(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (personalDetail?.surname) {
      onRegister(id, '');
    }
  }, [personalDetail?.surname]);

  useEffect(() => {
    if (registrationState === RegistrationState.Success) {
      navigate(PageRelativePath.Confirmation, {
        state: { journeyState: journey }
      });
    } else if (
      registrationState === RegistrationState.Failed ||
      registrationState === RegistrationState.VoidingPayment
    ) {
      navigate('../failedregistration');
    }
  }, [registrationState]);

  return (
    <>
      <Helmet>
        <title>{t('page-title.payment-succeeded')}</title>
      </Helmet>

      <div>
        <PageHeader title='Payment Assist Completed' description='' />
        <LoadingContainer
          loading={true}
          message='Please wait'></LoadingContainer>
      </div>
    </>
  );
};

export default PaymentAssistSuccess;
